// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  Fragment,
  h,
  Prop,
  State,
  Watch,
} from "@stencil/core";

import { parseValue } from "../utils";

export interface Data {
  email: string;
  userType: string;
}

export type Status = "error" | "pending" | "success";

@Component({
  tag: "qds-web-footer-subscription",
  shadow: true,
  styleUrl: "footer-subscription.css",
})
export class FooterSubscription implements ComponentInterface {
  /**
   * The text to display in the title.
   *
   * @public
   * @type {string}
   */
  @Prop() public text: string;

  /**
   * The text to display in the CTA.
   *
   * @public
   * @type {string}
   */
  @Prop() public ctaLabel?: string;

  /**
   * Value checked to disable the submit button when there is an extra validation step
   * in the <slot name="action"/>.
   *
   * By default it is set to false, the fixed "input-group" elements of the component have their own
   * validation that allows the submit action only if they are correctly completed.
   *
   * @remarks
   * If there are any extra elements that require validation to enable the button, for example a checkbox or
   * a captcha, the consumer must set this value to true. That way the submit button will be disabled until the
   * validation of these new elements is completed. This new validation must be done by the consumer, changing
   * the value of this prop from true to false when they have ensured the validation of the extra elements.
   *
   * Consumers must do their own validation of the action slot's additional elements, and must change
   * the value if they want this new elements to be part of the condition to enable the submit button.
   *
   * @public
   * @type {boolean}
   * @default false
   */
  @Prop() public disableSubscribe?: boolean = false;

  /**
   * Text to display as the select options. As HTML attributes can only be strings, the values array must be
   * stringified by the consumer into a string to be internally parsed into a string[].
   */
  @Prop() public selectOptions?: string[] | string;

  /**
   * The text to display in the input label.
   *
   * @public
   * @type {string}
   */
  @Prop() public inputLabel = "Email";

  /**
   * The text to display in the input placeholder.
   *
   * @public
   * @type {string}
   */
  @Prop() public inputPlaceholder = "name@email.com";

  /**
   * The text to display in the select label.
   *
   * @public
   * @type {string}
   */
  @Prop() public selectLabel = "I am a";

  /**
   * The text to display in the select default option.
   *
   * @public
   * @type {string}
   */
  @Prop() public selectDefaultOption = "Choose an option...";

  /**
   * Value that indicates the status of the request.
   *
   * By default it is set to "pending" as there won't be any request until the submit button is clicked.
   *
   * When the user submits the form, the status will still be "pending" until the server responds.
   * Once a response has been received, the consumer must set this value to "success" or "error"
   * depending on the response status.
   *
   * @remarks
   * If the request is successful, consumers must set this value to "success". And, conversely,
   * if the request was not successful, consumers must set this value to "error".
   *
   * @public
   * @type {string}
   * @default "pending"
   */
  @Prop() public status: Status = "pending";

  /**
   * The title text to display in the feedback message after the form is submitted.
   *
   * @remarks
   * Consumer must set the value depending on the response status.
   * If no titleFeedback is provided 'Thank you for joining us!' will be shown for a successful response
   * and 'Something went wrong on our side' for an error response.
   *
   * @public
   * @type {string}
   */
  @Prop() public titleFeedback: string;

  /**
   * The subtitle text to display in the feedback message after the form is submitted.
   *
   * @remarks
   * Consumer must set the value depending on the response status.
   * If no subtitleFeedback is provided, 'You have successfully subscribed to our newsletter' will be shown for a successful response
   * and 'Please try again later' for an error response.
   *
   * @public
   * @type {string}
   */
  @Prop() public subtitleFeedback: string;

  @Element() public element: HTMLElement;

  @Event({ eventName: "qdsSubmit", bubbles: false, cancelable: false })
  public qdsSubmit: EventEmitter<{
    email: string;
    userType: string;
  }>;

  @State() private hasSubmittedForm = false;

  @State() private hideActionSlot = false;

  @State() private hideSubscriptionSlot = false;

  @State() private readonly userData: Data = { email: "", userType: "" };

  @State() private computedIsSubscribeDisabled: true | undefined = true;

  @State() private parsedSelectOptions?: string[];

  public feedback?: HTMLElement;

  private readonly interval: number = 5000;

  get #computedIconName(): string {
    switch (this.status) {
      case "error": {
        return "exclamation-mark-circle";
      }
      case "success": {
        return "paper-airplane-3d";
      }
      default: {
        return "";
      }
    }
  }

  get #computedTitleFeedback(): string {
    if (!this.titleFeedback) {
      return this.status === "success"
        ? "Thank you for joining us!"
        : "Something went wrong on our side";
    }

    return this.titleFeedback;
  }

  get #computedSubtitleFeedback(): string {
    if (!this.subtitleFeedback) {
      return this.status === "success"
        ? "You have successfully subscribed to our newsletter"
        : "Please try again later";
    }

    return this.subtitleFeedback;
  }

  @Watch("disableSubscribe")
  public handleDisableSubscribeChange(): void {
    this.handleSubscribeDisabled();
  }

  @Watch("selectOptions")
  private selectOptionsChanged(newValue: string[] | string): void {
    this.parsedSelectOptions =
      typeof newValue === "string" ? parseValue(newValue) : newValue;
  }

  public handleOnSubmit(event: Event): void {
    event.preventDefault();

    this.qdsSubmit.emit({
      email: this.userData.email,
      userType: this.userData.userType,
    });

    this.handleSubscribeDisabled();

    this.hasSubmittedForm = true;
  }

  public handleOnInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const select = event.target as HTMLSelectElement;

    if (input.type === "email") {
      this.userData.email = input.value;
    } else {
      this.userData.userType = select.value;
    }

    this.handleSubscribeDisabled();
  }

  public componentWillLoad(): void {
    if (this.selectOptions !== undefined)
      this.selectOptionsChanged(this.selectOptions);
  }

  public componentWillRender(): Promise<void> | void {
    if (this.status === "error" && this.hasSubmittedForm) {
      this.handleOnErrorFeedback();
    }
  }

  public componentDidRender(): void {
    if (this.feedback !== undefined) {
      this.feedback.focus();
    }
  }

  public render() {
    const isFormDisabled = this.hasSubmittedForm && this.status === "pending";

    return (
      <form
        class="qds-footer-subscription"
        onSubmit={(event: Event) => {
          this.handleOnSubmit(event);
        }}
        aria-label={this.text}
      >
        <div class="text">
          <qds-title
            class="qds-title flex"
            level="subsection"
            layer="navigation"
          >
            {this.text}
          </qds-title>
          <div class={{ description: true, hidden: this.hideSubscriptionSlot }}>
            <slot onSlotchange={this.handleSlotChange} />
          </div>
        </div>
        <div
          class={{
            "form-groups": true,
            hidden: this.hasSubmittedForm && this.status !== "pending",
          }}
        >
          <div class={{ "action-group": true, hidden: this.hideActionSlot }}>
            <slot name="action" onSlotchange={this.handleSlotChange} />
          </div>
          <div class="input-group">
            <div class="control-group">
              <qds-label text={this.inputLabel} htmlFor="subscription-email" />
              <qds-input
                class="control"
                id="subscription-email"
                type="email"
                placeholder={this.inputPlaceholder}
                value={this.userData.email}
                onQdsInput={(event: Event) => {
                  this.handleOnInputChange(event);
                }}
                aria-invalid="false"
                required
                disabled={isFormDisabled}
              />
            </div>
            <div class="control-group">
              <qds-label
                text={this.selectLabel}
                htmlFor="subscription-user-type"
              />
              <qds-select
                class="control"
                id="subscription-user-type"
                text="Full width"
                value={this.userData.userType}
                onQdsInput={(event: Event) => {
                  this.handleOnInputChange(event);
                }}
                aria-invalid="false"
                required
                disabled={isFormDisabled}
              >
                <option value="" selected key={this.selectDefaultOption}>
                  {this.selectDefaultOption}
                </option>
                {this.parsedSelectOptions?.map((item) => (
                  <option value={item} key={item}>
                    {item}
                  </option>
                ))}
              </qds-select>
            </div>
            {(this.ctaLabel ?? "") && (
              <qds-button
                class="button"
                importance="emphasized"
                text={this.ctaLabel}
                disabled={this.computedIsSubscribeDisabled ?? isFormDisabled}
              />
            )}
          </div>
        </div>
        <div
          class={{
            feedback: true,
            hidden: !this.hasSubmittedForm || this.status === "pending",
          }}
          tabIndex={-1}
          ref={(element) => {
            this.feedback = element as HTMLElement;
          }}
        >
          {this.hasSubmittedForm && this.status !== "pending" && (
            <qds-title
              layer="navigation"
              level="subsection"
              tag="h3"
              icon-name={this.#computedIconName}
              subtitle={this.#computedSubtitleFeedback}
            >
              {this.#computedTitleFeedback}
            </qds-title>
          )}
        </div>
      </form>
    );
  }

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    if (slot.name === "action") {
      this.hideActionSlot = slot.assignedElements().length === 0;
    } else {
      this.hideSubscriptionSlot = slot.assignedElements().length === 0;
    }
  };

  private handleOnErrorFeedback(): void {
    setTimeout(() => {
      this.hasSubmittedForm = false;
      this.status = "pending";
    }, this.interval);
  }

  private handleSubscribeDisabled(): void {
    const inputsAreValid =
      this.userData.email !== "" && this.userData.userType !== "";

    if (this.disableSubscribe === false) {
      this.computedIsSubscribeDisabled = inputsAreValid ? undefined : true;
    }

    if (this.disableSubscribe === true) {
      this.computedIsSubscribeDisabled = true;
    }
  }
}
